/**
 * Shared push helpers used by the app shell and permission lifecycle.
 * Keeps the hook focused on orchestration instead of environment plumbing.
 */

/** URL-safe base64 VAPID public key, resolved once per page load. */
let vapidKeyPromise: Promise<string | undefined> | null = null;

export const pushLogger = {
  warn(message: string, error?: unknown) {
    if (error === undefined) {
      console.warn(`[Push] ${message}`);
      return;
    }
    console.warn(`[Push] ${message}:`, error);
  },
  error(message: string, error?: unknown) {
    if (error === undefined) {
      console.error(`[Push] ${message}`);
      return;
    }
    console.error(`[Push] ${message}:`, error);
  },
};

async function resolveVapidPublicKey(): Promise<string | undefined> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (fromEnv) return fromEnv;

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!supabaseUrl) {
      pushLogger.warn('VITE_SUPABASE_URL is not set – cannot fetch VAPID key');
      return undefined;
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/get-vapid-public-key`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = (await response.json()) as { publicKey?: string };
    return payload.publicKey;
  } catch (error) {
    pushLogger.error('Failed to fetch VAPID public key', error);
    return undefined;
  }
}

export function getVapidPublicKey(): Promise<string | undefined> {
  if (!vapidKeyPromise) {
    vapidKeyPromise = resolveVapidPublicKey();
  }
  return vapidKeyPromise;
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}
